import axios from 'axios'

const API_BASE = process.env.VUE_APP_API_URL || '/api'
const OVERVIEW_PATH = '/simulation-account/overview'
const OVERVIEW_ROUTE = '/api/simulation-account/overview'
const CONTRACT_VERSION = 'simulation_account_stage4_overview_rest_v1_20260805'

const client = axios.create({
  baseURL: API_BASE,
  timeout: 120000,
  headers: { 'Content-Type': 'application/json' }
})

client.interceptors.response.use(
  response => response.data,
  error => {
    const message = (error.response && error.response.data && error.response.data.detail) || error.message || '正式模拟账户请求失败'
    return Promise.reject(new Error(message))
  }
)

const PERFORMANCE_STATES = {
  active: { label: '已正式启用', type: 'success' },
  not_activated: { label: '未正式启用', type: 'info' },
  invalid_formal_baseline: { label: '正式基线待核验', type: 'warning' },
  invalid_formal_history: { label: '绩效历史待核验', type: 'warning' },
  unsupported_post_activation_cash_flow: { label: '资金变动后绩效待适配', type: 'warning' }
}

export function simulationAccountDisplayState(performance) {
  const scope = performance && performance.performance_scope
  return (scope && Object.prototype.hasOwnProperty.call(PERFORMANCE_STATES, scope.status) && PERFORMANCE_STATES[scope.status]) || { label: '绩效状态待核验', type: 'warning' }
}

function validatePerformanceScope(payload) {
  const performance = payload && payload.performance
  const scope = performance && performance.performance_scope
  const metrics = performance && performance.daily_snapshot_metrics
  const valuation = performance && performance.current_valuation
  const trade = performance && performance.trade_performance
  const account = performance && performance.account
  if (!scope || !metrics || !valuation || !trade || !account ||
      !Object.prototype.hasOwnProperty.call(PERFORMANCE_STATES, scope.status) ||
      payload.data_status !== performance.data_status ||
      metrics.performance_status !== scope.status) return false

  const active = scope.status === 'active'
  if (scope.business_enabled !== active || metrics.business_enabled !== active ||
      scope.history_integrity_verified !== active || metrics.history_integrity_verified !== active) return false

  if (active) {
    const baseline = scope.baseline
    return Boolean(baseline && typeof baseline.snapshot_id === 'string' && baseline.snapshot_id &&
      typeof baseline.trade_date === 'string' && baseline.trade_date &&
      metrics.baseline_snapshot_count === 1 &&
      ((performance.status === 'official' && performance.data_status === 'official' && performance.all_invariants_match === true) ||
       (performance.status === 'invariant_warning' && performance.data_status === 'provisional' && performance.all_invariants_match === false)))
  }

  // Known unavailable states remain readable, but cannot carry formal results.
  return performance.status === scope.status && performance.data_status === 'provisional' &&
    (scope.status !== 'not_activated' || scope.baseline === null) &&
    valuation.unit_nav === null && valuation.cumulative_pnl === null && valuation.cumulative_return === null &&
    account.realized_pnl_since_baseline === null && account.realized_return_on_capital === null &&
    trade.total_cycle_count === 0 && Array.isArray(trade.recent_cycles) && trade.recent_cycles.length === 0 &&
    metrics.snapshot_count === 0 && metrics.baseline_snapshot_count === 0 &&
    Array.isArray(metrics.recent_snapshots) && metrics.recent_snapshots.length === 0
}

export function validatePayload(payload) {
  const contract = payload && payload.page_contract
  const performance = payload && payload.performance
  const account = performance && performance.account
  const separation = performance && performance.strict_separation
  const valid = Boolean(
    payload &&
    payload.ok === true &&
    payload.payload_type === 'simulation_account_stage4_overview' &&
    validatePerformanceScope(payload) &&
    performance &&
    performance.ok === true &&
    performance.read_only === true &&
    performance.writes_database === false &&
    performance.calls_provider === false &&
    performance.starts_scheduler === false &&
    performance.starts_worker === false &&
    performance.mutates_queue === false &&
    performance.sends_notification === false &&
    account &&
    typeof account.account_id === 'string' &&
    account.account_id.indexOf('SIM-ACCOUNT-') === 0 &&
    Array.isArray(payload.current_positions) &&
    Array.isArray(payload.recent_events) &&
    Array.isArray(payload.account_flow_events) &&
    Array.isArray(payload.data_gaps) &&
    contract &&
    contract.route === OVERVIEW_ROUTE &&
    contract.contract_version === CONTRACT_VERSION &&
    contract.stage === 'stage_4_formal_simulation_account_frontend' &&
    contract.read_only === true &&
    contract.cache_policy === 'no_store' &&
    contract.formal_account_only === true &&
    contract.legacy_rows_excluded_by_account_id === true &&
    contract.full_long_ids_required === true &&
    contract.direct_account_mutation_enabled === false &&
    contract.preview_confirm_execute_chain_bypass_enabled === false &&
    contract.writes_database === false &&
    contract.calls_provider === false &&
    contract.starts_worker === false &&
    contract.starts_scheduler === false &&
    contract.mutates_queue === false &&
    contract.sends_notification === false &&
    contract.writes_first_book === false &&
    contract.writes_real_position === false &&
    contract.creates_real_order === false &&
    contract.auto_trade === false &&
    contract.snapshot_binding_installed === true &&
    separation &&
    separation.writes_first_book === false &&
    separation.writes_real_position === false &&
    separation.creates_real_order === false &&
    separation.auto_trade === false &&
    separation.legacy_rows_count_toward_metrics === false
  )
  if (!valid) throw new Error('正式模拟账户只读合同校验失败')
  return payload
}

export const simulationAccountApi = {
  async getOverview() {
    const payload = await client.get(OVERVIEW_PATH, {
      params: { _: Date.now() },
      headers: { 'Cache-Control': 'no-cache' }
    })
    return validatePayload(payload)
  }
}

export function simulationAccountErrorMessage(error, fallback) {
  return (error && error.message) || fallback || '正式模拟账户请求失败'
}
